/**
 * API for all Company settings:
 * Brands, etc.
 */
import snakeCase from 'lodash-es/snakeCase';

import { ServerListViewChangeData } from '@/components/ServerListView/ServerListViewChangeData';
import { AUTHORIZE_NET_URL, URLS } from '@/constants';
import { PaginatedResponse } from '@/models/api';
import { BillingInvoice, BillingPaymentMethod, GetBillingInvoicesArgs } from '@/models/billing';
import { Application, CompanyLicense, License, LicenseType } from '@/models/company';
import { Distributor } from '@/models/distributor';
import {
  CompanyActiveIntegration,
  CompanyIntegration,
  CreateBrandData,
  GetBrandsParams,
  IntegratedSystem,
  IntegrationFieldType,
  IntegrationSettingResponse,
  InviteBrand,
  InviteBrandForm,
  UpdateBrandData,
} from '@/models/settings';
import { Staff, StaffWithPermissions } from '@/models/staff';
import { Store, StorePayload } from '@/models/store';
import { Timezone } from '@/models/timezone';
import { TaxSchedule, TaxSchedulePayload } from '@/pages/Settings/Taxes/Taxes.models';
import { useCompanyStore } from '@/piniaStores/company/company.store';
import { useUserStore } from '@/piniaStores/user/user.store';
import {
  AvailableCompanyPermissionsResponse,
  CompanyBillingRequestData,
  CompanyBillingResponseData,
  CreateStaffUserPayload,
  InviteStaffUserPayload,
  PostPOSIntegrationRequestData,
  PostPOSIntegrationResponseData,
  SettingsBrand,
  UpdateStaffUserPayload,
} from '@/services/api/models/settings';
import { TimezoneResponse } from '@/services/api/models/timezone';
import { buildPath } from '@/utils';
import JSONTransform from '@/utils/json-transform';

import session, { apiSlugSession, createSession, slugSession, unifiedSession } from '../session';

/**
 * Fetches authorize.net unique accept customer token.
 */
export const getAcceptCustomerToken = () => {
  return apiSlugSession
    .get(`${URLS.SETTINGS.BILLING.BASE}/${URLS.SETTINGS.BILLING.AUTHORIZE.ACCEPT_CUSTOMER}?${URLS.CUSTOM_ERROR}`)
    .then((response) => response.data);
};

/**
 * Fetches authorize.net unique accept host token.
 */
export const getAcceptHostToken = (invoice: BillingInvoice) => {
  const userStore = useUserStore();

  return createSession(URLS.INTERNAL + userStore.userActiveCompany?.slug, { shouldTransform: false })
    .post(`${URLS.SETTINGS.BILLING.BASE}/${URLS.SETTINGS.BILLING.AUTHORIZE.ACCEPT_HOST}?${URLS.CUSTOM_ERROR}`, invoice)
    .then((response) => response.data);
};

/**
 * Fetches Billing Invoice from backend
 */
export const getBillingInvoices = async (args?: GetBillingInvoicesArgs) => {
  const url = `${URLS.SETTINGS.BILLING.BASE}/${URLS.SETTINGS.BILLING.INVOICES}/`;
  const params = { error_format: 'jsonapi', ...args };
  const response = await apiSlugSession.get<PaginatedResponse<BillingInvoice>>(url, { params });
  return response.data;
};

/**
 * Fetches Billing Invoice using the v2 endpoint
 */
export const getBillingInvoicesV2 = async (args?: GetBillingInvoicesArgs) => {
  const url = `${URLS.SETTINGS.BILLING.BASE}/v2/${URLS.SETTINGS.BILLING.INVOICES}/`;

  const params = new JSONTransform({
    ...args,
    invoiceStatus: args?.invoiceStatus?.join(','),
    error_format: 'jsonapi',
  }).toSnakeCaseKeys();

  const response = await apiSlugSession.get<PaginatedResponse<BillingInvoice>>(url, { params });
  return response.data;
};

/**
 * Edits a company's billing payment method
 */
export const updateBillingPaymentMethod = (paymentMethodId: string, isDefault: boolean) => {
  return apiSlugSession
    .patch(`${URLS.SETTINGS.BILLING.BASE}/${URLS.SETTINGS.BILLING.PAYMENT_METHODS}/${paymentMethodId}/`, {
      is_default: isDefault,
    })
    .then((response) => response.data);
};

/**
 * Creates a bank account for a company
 */
export const createCompanyBankAccount = async (bankAccount: {
  email: string;
  publicToken: string;
  isDefault: boolean;
}) => {
  const response = await apiSlugSession.post(
    `${URLS.SETTINGS.BILLING.BASE}/${URLS.SETTINGS.BILLING.BANK_ACCOUNTS}/?${URLS.CUSTOM_ERROR}`,
    bankAccount,
  );

  return response.data;
};

/**
 * Fetches Billing Invoice from salesforce
 */
export const setBillingStatusPending = (invoiceId: string) => {
  return apiSlugSession
    .patch(
      `${URLS.SETTINGS.BILLING.BASE}/${URLS.SETTINGS.BILLING.INVOICES}/${invoiceId}/${URLS.SETTINGS.BILLING.MAKE_PENDING}/?${URLS.CUSTOM_ERROR}`,
    )
    .then((response) => response.data);
};

export interface PaymentProfile {
  id: string;
  type: string;
  bankName: string;
  accountNumber: string;
  cardType: string;
  cardNumber: string;
}
/**
 * Fetches payment profiles from our backend
 */
export const getPaymentProfiles = async () => {
  const response = await apiSlugSession.get<PaymentProfile[]>(
    `${URLS.SETTINGS.BILLING.BASE}/${URLS.SETTINGS.BILLING.AUTHORIZE.PAYMENT_PROFILES}?${URLS.CUSTOM_ERROR}`,
  );

  return response.data;
};

/**
 * Execute pay invoice action
 */
export const payInvoice = (invoiceId: string | number, paymentProfileId: string) => {
  return apiSlugSession
    .patch(
      `${URLS.SETTINGS.BILLING.BASE}/${URLS.SETTINGS.BILLING.INVOICES}/${invoiceId}/${URLS.SETTINGS.BILLING.PAY}/?${URLS.CUSTOM_ERROR}`,
      { payment_profile_id: paymentProfileId },
    )
    .then((response) => response.data);
};

export interface PayInvoiceV2RequestArgs {
  invoiceId: BillingInvoice['id'];
  paymentMethodId: BillingPaymentMethod['id'];
  paymentMethodType: BillingPaymentMethod['contentName'];
}

/**
 * Execute pay invoice action
 */
export const payInvoiceV2 = async (args: PayInvoiceV2RequestArgs) => {
  const url = `${URLS.SETTINGS.BILLING.BASE}/${URLS.SETTINGS.BILLING.INVOICES}/${args.invoiceId}/${URLS.SETTINGS.BILLING.PAY}/`;
  const data = { id: args.paymentMethodId };
  const params = { error_format: 'jsonapi', type: args.paymentMethodType };
  const response = await apiSlugSession.patch(url, data, { params });
  return response.data;
};

/**
 * Authorize.net redirect to Accept Customer form.
 */
export const getEditPaymentForm = (token: string) => {
  return apiSlugSession.post(`${AUTHORIZE_NET_URL.EDIT_PAYMENT}`, token).then((response) => response.data);
};

/**
 * Authorize.net redirect to Accept Host form.
 */
export const getMakePaymentForm = (token: string) => {
  return apiSlugSession.post(`${AUTHORIZE_NET_URL.MAKE_PAYMENT}`, token).then((response) => response.data);
};

/**
 * Fetches all Brands of a given Company.
 */
export const getBrandOwnerBrands = () => {
  const params = {
    company_slug: '', // Company/index.vue adds a company_slug query parameter, so this line is removing it
  };

  return apiSlugSession
    .get(`${URLS.BRAND_OWNER.BASE}/${URLS.BRAND_OWNER.BRANDS}`, { params })
    .then((response) => response.data.results);
};

/**
 * Fetches all Brands of a given Company.
 */
export const getBrands = async (params: GetBrandsParams) => {
  const paramsFormatted = new JSONTransform({ errorFormat: 'jsonapi', ...params }).toSnakeCaseKeys();
  const response = await session.get<PaginatedResponse<SettingsBrand>>(URLS.SETTINGS.BRANDS, {
    params: paramsFormatted,
  });

  return response.data;
};

/**
 * Get Brand
 * @return Promise
 */
export const getBrand = async (brandId: number) => {
  const path = buildPath(URLS.SETTINGS.BRAND, { id: brandId });
  const response = await session.get<SettingsBrand>(path, { params: { error_format: 'jsonapi' } });

  return response.data;
};

/**
 * Creates a new brand
 */
export const createBrand = async (brand: CreateBrandData) => {
  const formSession = createSession(URLS.INTERNAL, { shouldTransform: false });
  const headers = { 'Content-Type': 'multipart/form-data' };

  const formData = new FormData();

  Object.entries(brand)
    .filter(([_, v]) => v !== undefined)
    .forEach(([key, value]) => {
      formData.append(snakeCase(key), value);
    });

  const response = await formSession.post<SettingsBrand>(URLS.SETTINGS.BRANDS, formData, {
    headers,
    params: { error_format: 'jsonapi' },
  });
  return response.data;
};

/**
 * Updates the brand information
 */
export const updateBrand = async (id: string, brand: UpdateBrandData) => {
  const path = buildPath(URLS.SETTINGS.BRAND, { id });
  const formSession = createSession(URLS.INTERNAL, { shouldTransform: false });
  const headers = { 'Content-Type': 'multipart/form-data' };

  const formData = new FormData();

  Object.entries(brand)
    .filter(([_, v]) => v !== undefined)
    .forEach(([key, value]) => {
      formData.append(snakeCase(key), value);
    });

  const response = await formSession.patch<SettingsBrand>(path, formData, {
    headers,
    params: { error_format: 'jsonapi' },
  });
  return response.data;
};

/**
 * Invite a new manager to brand
 */
export const inviteBrandManager = async (invite: InviteBrandForm) => {
  const response = await session.post<InviteBrand>(`${URLS.SETTINGS.INVITE_BRAND}/`, invite);

  return response.data;
};

/**
 * Accept a brand invitation
 */
export const acceptBrandInvitation = async (inviteId: number, masterBrandName: string) => {
  const response = await session.patch(
    buildPath(URLS.SETTINGS.INVITE_BRAND_ACCEPT, { inviteId }),
    { masterBrandName },
    { params: { error_format: 'jsonapi' } },
  );

  return response.data;
};

/**
 * Reject a brand invitation
 */
export const rejectBrandInvitation = async (inviteId: number) => {
  const path = buildPath(URLS.SETTINGS.INVITE_BRAND_REJECT, { inviteId });
  const response = await session.patch(`${path}/?${URLS.CUSTOM_ERROR}`);

  return response.data;
};

/**
 * Fetches a list of company licenses.
 */
export const getLicenses = async () => {
  const url = URLS.COMPANY.LICENSES;
  const params = new JSONTransform({ errorFormat: 'jsonapi' }).toSnakeCaseKeys();
  const response = await session.get<PaginatedResponse<License>>(url, { params });

  return response.data;
};

/**
 * Get Sales Reps by current Company.
 * @param params.active - whether or not to filter by active staff
 * @return Promise
 */
export const getStaff = async ({ active }: { active: boolean }) => {
  const response = await session.get<Staff[]>(`${URLS.COMPANY.STAFF}${active ? '/active' : ''}/?${URLS.CUSTOM_ERROR}`);

  return response.data;
};

/**
 * Get available user permissions for current company
 */
export const getAvailableCompanyPermissions = async () => {
  const response = await session.get<AvailableCompanyPermissionsResponse>(
    `${URLS.SETTINGS.AVAILABLE_PERMISSIONS}/?${URLS.CUSTOM_ERROR}`,
  );

  return response.data;
};

/**
 * Get active and deactivated staff members using company-staffs url
 */
export const getStaffWithPermissions = ({
  params = 'limit=9999',
}: {
  params?: ServerListViewChangeData['serialized'];
}) => {
  return session
    .get<PaginatedResponse<StaffWithPermissions>>(
      `${URLS.COMPANY.STAFF_WITH_PERMISSIONS}/?${URLS.CUSTOM_ERROR}&${params}`,
    )
    .then((response) => response.data.results);
};

/**
 * Invite Staff User
 */
export const inviteStaffUser = async (staffUser: Partial<InviteStaffUserPayload>) => {
  const response = await session.post(`${URLS.COMPANY.STAFF_WITH_PERMISSIONS}/?${URLS.CUSTOM_ERROR}`, staffUser);

  return response.data;
};

/**
 * Create Staff User
 */
export const createStaffUser = async (staffUser: Partial<CreateStaffUserPayload>) => {
  const response = await session.post(`${URLS.COMPANY.STAFF_WITH_PERMISSIONS}/?${URLS.CUSTOM_ERROR}`, staffUser);

  return response.data;
};

/**
 * Update Staff User
 */
export const updateStaffUser = async (staffUser: UpdateStaffUserPayload) => {
  const path = buildPath(URLS.COMPANY.STAFF_WITH_PERMISSIONS_EDIT, { id: staffUser.id });
  const response = await session.patch<StaffWithPermissions>(path, staffUser, { params: { error_format: 'jsonapi' } });

  return response.data;
};

export const getStaffById = (id: number | `${number}`) => {
  return session
    .get<StaffWithPermissions>(`${URLS.COMPANY.STAFF_WITH_PERMISSIONS}/${id}/?${URLS.CUSTOM_ERROR}`)
    .then((response) => response.data);
};

export const fetchTimezones = async () => {
  const response = await apiSlugSession.get<PaginatedResponse<TimezoneResponse>>(
    `${URLS.SETTINGS.TIMEZONES}/?${URLS.CUSTOM_ERROR}`,
  );

  return response.data.results.map<Timezone>(({ timezone, id }) => ({ id, name: timezone }));
};

/**
 * Fetches Brand Manager distributors
 */
export const getDistributors = async () => {
  const response = await apiSlugSession.get<PaginatedResponse<Distributor>>(
    `${URLS.BRAND_OWNER.BASE}/${URLS.BRAND_OWNER.DISTRIBUTORS}/?${URLS.CUSTOM_ERROR}`,
  );

  return response.data.results;
};

/**
 * Get Applications by current company
 * @return Promise
 */
export const getApplications = () => {
  return apiSlugSession.get(`${URLS.COMPANY.APPLICATIONS}/?${URLS.CUSTOM_ERROR}`).then((response) => response.data);
};

/**
 * Add new application
 * @return Promise
 */
export const addApplication = (application: Application) => {
  return apiSlugSession
    .post(`${URLS.COMPANY.APPLICATIONS}/?${URLS.CUSTOM_ERROR}`, application)
    .then((response) => response.data);
};

/**
 * Save existing application
 * @return Promise
 */
export const saveApplication = (application: Application) => {
  return apiSlugSession
    .patch(`${URLS.COMPANY.APPLICATIONS}/${application.id}/?${URLS.CUSTOM_ERROR}`, application)
    .then((response) => response.data);
};

/**
 * Delete existing application
 * @return Promise
 */
export const deleteApplication = (id) => {
  return apiSlugSession
    .delete(`${URLS.COMPANY.APPLICATIONS}/${id}/?${URLS.CUSTOM_ERROR}`)
    .then((response) => response.data);
};

/**
 * Regenerate token for existing application
 * @return Promise
 */
export const regenerateApplicationToken = (id) => {
  return apiSlugSession
    .post(`${URLS.COMPANY.APPLICATIONS}/${id}/${URLS.COMPANY.REGENERATE_TOKEN}/`)
    .then((response) => response.data);
};

/**
 * Get All payment options
 * @return Promise
 */
export const getPaymentOptionsSettings = async () => {
  const [paymentMethods, paymentTerms] = await Promise.all([
    session.get(`${URLS.SETTINGS.PAYMENT_METHODS}/?${URLS.CUSTOM_ERROR}`),
    session.get(`${URLS.SETTINGS.PAYMENT_TERMS}/?${URLS.CUSTOM_ERROR}`),
  ]);

  return {
    paymentMethods: paymentMethods.data,
    paymentTerms: paymentTerms.data,
  };
};

/**
 * Get all available integrations
 */
export const getIntegratedSystems = async (params: Record<string, string> = {}) => {
  const response = await session.get<{ results: IntegratedSystem[] }>(`${URLS.INTEGRATIONS.SYSTEMS}/`, {
    params: { ...params },
  });

  return response.data.results;
};

/**
 * Get integration data for active company
 */
export const getCompanyIntegrations = async (params: Record<string, string> = {}) => {
  const response = await session.get<{ results: CompanyIntegration[] }>(`${URLS.INTEGRATIONS.BASE}/`, {
    params: { ...params },
  });
  return response.data.results;
};

export const getCompanyActiveIntegration = async (): Promise<Array<CompanyActiveIntegration>> => {
  const response = await session.get(URLS.COMPANY.POS_INTEGRATION);
  return response.data;
};

/**
 * Creates an integration
 */
export const createCompanyIntegration = async (data: {
  company: number;
  integratedSystem: number;
  licenses?: number[];
}) => {
  const response = await session.post<CompanyIntegration>(`${URLS.INTEGRATIONS.BASE}/`, data);

  return response.data;
};

/**
 * Creates an integration setting
 */
export const createCompanyIntegrationSetting = async (args: {
  integrationId: number;
  setting: {
    key: IntegrationFieldType;
    value: string;
  };
}) => {
  const { integrationId, setting } = args;
  const { key, value } = setting;
  const response = await session.post<IntegrationSettingResponse>(
    `${URLS.INTEGRATIONS.BASE}/${integrationId}/settings/`,
    { key, value },
  );

  return response.data;
};

export const createCompanyPOSIntegration = async (
  data: PostPOSIntegrationRequestData,
): Promise<PostPOSIntegrationResponseData> => {
  return await session
    .post<PostPOSIntegrationResponseData>(URLS.COMPANY.POS_INTEGRATION, data)
    .then((response) => response.data);
};

/**
 * Updates an integration setting
 */
export const updateCompanyIntegrationSetting = async (args: { settingId: number; value: string }) => {
  const { settingId, value } = args;
  const response = await session.patch<IntegrationSettingResponse>(`${URLS.INTEGRATIONS.SETTINGS}/${settingId}/`, {
    value,
  });

  return response.data;
};

/**
 * Updates an integration licenses
 */
export const updateIntegrationLicenses = async (args: {
  id: string | number;
  data: {
    licenses: number[];
  };
}) => {
  const { data, id } = args;
  const response = await session.patch<CompanyIntegration>(`${URLS.INTEGRATIONS.BASE}/${id}/`, data);

  return response.data;
};

/**
 * Removes an integration
 */
// eslint-disable-next-line require-await
export const deleteCompanyIntegration = async (id: number) => {
  return session.delete(`${URLS.INTEGRATIONS.BASE}/${id}/`);
};

/**
 * Removes a POS integration
 */
// eslint-disable-next-line require-await
export const deletePosCompanyIntegration = async (currentIntegrationName: string) => {
  return session.delete(`${URLS.COMPANY.POS_INTEGRATION}`, {
    data: {
      integrated_system_name: currentIntegrationName,
    },
  });
};

/**
 * Gets array of license types active for registration in state
 * @param state - state to retrieve license types from
 */
export const getLicenseTypes = async (state: LicenseType['state']) => {
  const response = await apiSlugSession.get<PaginatedResponse<LicenseType>>(
    `${URLS.SETTINGS.LICENSES.LICENSE_TYPES}/?state=${state}`,
  );

  return response.data.results;
};

/**
 * Archive company license
 * @param companyLicenseId - id for company license to archive
 */
export const archiveCompanyLicense = async (companyLicenseId: CompanyLicense['companyLicenseId']) => {
  const response = await slugSession.post<CompanyLicense>(
    `${URLS.SETTINGS.LICENSES.BASE}/${companyLicenseId}/${URLS.SETTINGS.LICENSES.TOGGLE_ARCHIVE}/`,
  );

  return response.data;
};

export const createLicenseChangeRequest = (params: FormData) => {
  const formSession = createSession(URLS.INTERNAL, { shouldTransform: false });

  return formSession.post(`${URLS.SETTINGS.LICENSES.LICENSE_CHANGE_REQUESTS}/`, params);
};

export interface LeaflinkStates {
  id: number;
  abbr: string;
  name: string;
  country: {
    id: 1;
    abbr: string;
    isoCode: string;
    name: string;
  };
}

/**
 * Get a list of states that have leaflink can operate in
 * @return {Promise<LeaflinkStates[]>}
 */
export const getLeaflinkStates = async (slug: string) => {
  const response = await session.get<LeaflinkStates[]>(`${slug}/${URLS.STATES.LIST}`);

  return response.data;
};

/**
 * Create a new store
 */
export const createStore = async (store: StorePayload) => {
  const response = await session.post(`${URLS.COMPANY.STORE}/?${URLS.CUSTOM_ERROR}`, store);

  return response.data;
};

/**
 * Add store license
 */
export const updateLicense = async (id: number, license: File) => {
  const path = buildPath(URLS.COMPANY.STORE_EDIT, { id });
  const url = `${path}/?${URLS.CUSTOM_ERROR}`;
  const headers = { 'Content-Type': 'multipart/form-data' };

  const formData = new FormData();

  formData.append('license_file', license);

  const response = await session.patch<Store>(url, formData, { headers });
  return response.data;
};

/**
 * Fetches a list the store's pending requests.
 * this endpoint is mocked until the BE creates the real one.
 * We might need to update the interface for it later.
 */
export const getStorePendingRequests = async () => {
  const response = await session.get(URLS.COMPANY.STORE, {
    params: { error_format: 'jsonapi', status: 'pending' },
  });

  return response.data;
};

export interface AddNewStoreRequest {
  name: string;
  phone: string;
  address1: string;
  address2?: string; // or unitNumber
  city: string;
  state: string;
  zipcode: string;
  licenseNumber: string;
  licenseType?: number;
  issueDate: string;
  expirationDate: string;
  notes: string | null;
  licenseFile: string; // pending decision on this
  companyPOSSystem: string;
  comments: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const addNewStoreOrLicense = (slug: string, storeOrLicense: AddNewStoreRequest) => {
  return storeOrLicense;
};

// Fetches a paginated list of a company's tax schedules
export const getTaxSchedules = async () => {
  const { activeCompany } = useCompanyStore();

  const response = await unifiedSession.get<PaginatedResponse<TaxSchedule>>(URLS.SETTINGS.TAXES.TAX_SCHEDULES, {
    params: {
      company_id: activeCompany?.id,
    },
  });

  return response.data;
};

export const updateTaxSchedule = async (id: number, taxSchedule: TaxSchedulePayload) => {
  const { activeCompany } = useCompanyStore();

  const response = await unifiedSession.patch(
    `${URLS.SETTINGS.TAXES.TAX_SCHEDULES}/${id}`,
    {
      ...taxSchedule,
    },
    {
      params: {
        company_id: activeCompany?.id,
      },
    },
  );

  return response.data;
};

export const postTaxSchedule = async (taxSchedule: TaxSchedulePayload) => {
  const { activeCompany } = useCompanyStore();
  const response = await unifiedSession.post(
    URLS.SETTINGS.TAXES.TAX_SCHEDULES,
    {
      ...taxSchedule,
    },
    {
      params: {
        company_id: activeCompany?.id,
      },
    },
  );

  return response.data;
};

export const deleteTaxSchedule = async (id: number) => {
  const { activeCompany } = useCompanyStore();
  const response = await unifiedSession.delete(`${URLS.SETTINGS.TAXES.TAX_SCHEDULES}/${id}`, {
    params: {
      company_id: activeCompany?.id,
    },
  });

  return response.data;
};

export const getBillingPaymentMethods = async () => {
  const url = `${URLS.SETTINGS.BILLING.BASE}/${URLS.SETTINGS.BILLING.PAYMENT_METHODS}/`;
  const params = { error_format: 'jsonapi' };
  const response = await apiSlugSession.get<BillingPaymentMethod[]>(url, { params });
  return response.data;
};

export const deleteCompanyPaymentMethod = async (id: string) => {
  const response = await apiSlugSession.delete(
    `${URLS.SETTINGS.BILLING.BASE}/${URLS.SETTINGS.BILLING.PAYMENT_METHODS}/${id}/?${URLS.CUSTOM_ERROR}`,
  );
  return response.data;
};

export const setCompanyPaymentMethodToDefault = async (id: string) => {
  const response = await apiSlugSession.patch(
    `${URLS.SETTINGS.BILLING.BASE}/${URLS.SETTINGS.BILLING.PAYMENT_METHODS}/${id}/?${URLS.CUSTOM_ERROR}`,
    { is_default: true },
  );
  return response.data;
};

export const postValidatePOSIntegration = async (
  data: PostPOSIntegrationRequestData,
): Promise<PostPOSIntegrationResponseData> => {
  return await session
    .post<PostPOSIntegrationResponseData>(`${URLS.ONBOARDING.POS_INTEGRATION_VALIDATE}`, data)
    .then((response) => response.data);
};

export const getCompanyBillingWithPost = async () => {
  return await apiSlugSession
    .post<CompanyBillingResponseData>(`${URLS.ONBOARDING.COMPANY_BILLING}`)
    .then((response) => response.data);
};

export const patchCompanyBilling = async (data: CompanyBillingRequestData) => {
  return await apiSlugSession
    .patch<CompanyBillingResponseData>(`${URLS.ONBOARDING.COMPANY_BILLING}${data.id}`, {
      billingEmail: data.billingEmail,
    })
    .then((response) => response.data);
};
